import { useEffect, useRef, useState } from 'react';
import { Zap, Check, X, AlertTriangle, RefreshCw, Clock } from 'lucide-react';
import PetanqueLoader from './PetanqueLoader';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog } from './ui/dialog';
import { api } from '../api/sangly';
import { moneyExact } from '../lib/format';
import { cn } from '../lib/utils';

/**
 * Pay several pro-formas in one go. Each one is pre-checked first (amount,
 * vendor bank account, address, communication, not already in payment) so the
 * ones that would be refused are set aside with a reason instead of failing
 * halfway; the ready ones are then paid one after the other via Airwallex,
 * with a live per-item result.
 */
const STATE = {
  queued: { label: 'Queued', icon: Clock, tone: 'text-muted-foreground' },
  checking: { label: 'Checking…', icon: RefreshCw, tone: 'text-muted-foreground' },
  ready: { label: 'Ready', icon: Check, tone: 'text-emerald-600' },
  skipped: { label: 'Needs attention', icon: AlertTriangle, tone: 'text-amber-600' },
  paying: { label: 'Paying…', icon: RefreshCw, tone: 'text-primary' },
  sent: { label: 'Sent to Airwallex', icon: Zap, tone: 'text-emerald-600' },
  failed: { label: 'Refused', icon: X, tone: 'text-rose-600' },
};

// what a row is called on screen - a vendor (pro-forma) or a customer (refund)
const nameOf = (r) => r.supplier_name || r.client || '-';
const refOf = (r) => r.their_reference || r.credit_note_ref || r.our_reference || '';

function preflight(r, info, isRefund) {
  const reasons = [];
  const b = info.beneficiary;
  const account = b?.accounts?.[0];
  if (!(Number(r.amount) > 0)) reasons.push('amount not filled in');
  if (r.in_payment) reasons.push('already in payment');
  if (!info.airwallex?.enabled) reasons.push('Airwallex not connected');
  if (!b) reasons.push(info.beneficiary_error || (isRefund ? 'no customer details' : 'no vendor details'));
  else if (!account) reasons.push(isRefund ? 'no IBAN typed on the refund' : 'no IBAN on the vendor in Odoo');
  if (!(b?.street && b?.zip && b?.city)) reasons.push(isRefund ? 'address unknown - pay it alone to fill it in' : 'address incomplete in Odoo');
  if (isRefund && !(b?.name || '').trim()) reasons.push('no name');
  if (!(info.reference || '').trim()) reasons.push('no reference');
  (info.warnings || []).forEach((w) => { if (/looks wrong/i.test(w)) reasons.push('IBAN looks wrong'); });
  return { reasons: [...new Set(reasons)], account, entity: info.entity_type };
}

// kind: 'proforma' (default) | 'refund'
export default function BulkPayDialog({ open, items, kind = 'proforma', onClose, onDone }) {
  const isRefund = kind === 'refund';
  const svc = isRefund ? api.refunds : api.proformas;
  const noun = isRefund ? 'refund' : 'pro-forma';
  const [rows, setRows] = useState([]);
  const [phase, setPhase] = useState('check');   // check | confirm | pay | done
  const [checked, setChecked] = useState(false);
  const cancelled = useRef(false);

  useEffect(() => {
    if (!open) return undefined;
    cancelled.current = false;
    setChecked(false); setPhase('check');
    const initial = items.map((pf) => ({ pf, state: 'queued', reasons: [], account: null, entity: '', result: null }));
    setRows(initial);
    (async () => {
      for (let i = 0; i < initial.length; i += 1) {
        if (cancelled.current) return;
        setRows((rs) => rs.map((r, j) => (j === i ? { ...r, state: 'checking' } : r)));
        let next;
        try {
          const info = await svc.paymentInfo(initial[i].pf.id);
          const { reasons, account, entity } = preflight(initial[i].pf, info, isRefund);
          next = { state: reasons.length ? 'skipped' : 'ready', reasons, account, entity };
        } catch (e) {
          next = { state: 'skipped', reasons: [e?.response?.data?.detail || 'could not load the payment sheet'], account: null };
        }
        setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...next } : r)));
      }
      setPhase('confirm');
    })();
    return () => { cancelled.current = true; };
  }, [open, items]); // eslint-disable-line react-hooks/exhaustive-deps -- kind is fixed per dialog

  const ready = rows.filter((r) => r.state === 'ready');
  const totals = ready.reduce((acc, r) => { acc[r.pf.currency] = (acc[r.pf.currency] || 0) + Number(r.pf.amount); return acc; }, {});
  const totalLabel = Object.entries(totals).map(([c, a]) => moneyExact(a, c)).join(' + ');

  const payAll = async () => {
    setPhase('pay');
    const list = rows.map((r, i) => ({ r, i })).filter(({ r }) => r.state === 'ready');
    for (const { r, i } of list) {
      if (cancelled.current) return;
      setRows((rs) => rs.map((x, j) => (j === i ? { ...x, state: 'paying' } : x)));
      try {
        const p = isRefund
          ? await svc.pay(r.pf.id, { account_id: r.account?.id ?? null, entity_type: r.entity || '' })
          : await svc.pay(r.pf.id, { via: 'airwallex', account_id: r.account?.id ?? null });
        setRows((rs) => rs.map((x, j) => (j === i ? { ...x, state: 'sent', result: p } : x)));
      } catch (e) {
        const detail = e?.response?.data?.detail || 'refused';
        setRows((rs) => rs.map((x, j) => (j === i ? { ...x, state: 'failed', reasons: [detail] } : x)));
      }
    }
    setPhase('done');
    onDone?.();
  };

  const counts = rows.reduce((acc, r) => { acc[r.state] = (acc[r.state] || 0) + 1; return acc; }, {});

  return (
    <Dialog open={open} onClose={phase === 'pay' ? undefined : onClose} className="max-w-2xl p-0">
      <div className="p-4 pr-10 sm:p-5 sm:pr-11">
        <div className="flex items-center gap-2 text-lg font-semibold"><Zap className="h-5 w-5 text-primary" /> Pay {items.length} {noun}{items.length === 1 ? '' : 's'}</div>
        <div className="mt-0.5 text-[12px] text-muted-foreground">
          {phase === 'check' && `Checking each one: amount, ${isRefund ? 'IBAN, customer address' : 'vendor bank account, address'}, communication…`}
          {phase === 'confirm' && `${ready.length} ready${counts.skipped ? ` · ${counts.skipped} need attention (open them to fix)` : ''}`}
          {phase === 'pay' && 'Sending the transfers one by one - keep this open.'}
          {phase === 'done' && `${counts.sent || 0} sent to Airwallex${counts.failed ? ` · ${counts.failed} refused` : ''}${counts.skipped ? ` · ${counts.skipped} set aside` : ''}`}
        </div>
      </div>

      <div className="max-h-[55vh] overflow-y-auto border-y border-border">
        <ol className="divide-y divide-border/70">
          {rows.map((r) => {
            const st = STATE[r.state];
            const Icon = st.icon;
            return (
              <li key={r.pf.id} className="flex items-start gap-3 px-4 py-2.5 sm:px-5">
                <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', st.tone, (r.state === 'checking' || r.state === 'paying') && 'animate-spin')} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 text-[13px]">
                    <span className="font-medium">{nameOf(r.pf)}</span>
                    {refOf(r.pf) && <span className="font-mono text-[12px] text-muted-foreground">{refOf(r.pf)}</span>}
                    <span className="ml-auto font-semibold tnum">{Number(r.pf.amount) > 0 ? moneyExact(r.pf.amount, r.pf.currency) : 'amount?'}</span>
                  </div>
                  <div className={cn('text-[12px]', st.tone)}>
                    {st.label}
                    {r.state === 'sent' && r.result && <> · <span className="font-mono">{r.result.airwallex_short_reference}</span> {r.result.airwallex_status}</>}
                    {r.reasons.length > 0 && <> · {r.reasons.join(', ')}</>}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="px-4 py-3 sm:px-5">
        {phase === 'confirm' && ready.length > 0 && (
          <label className="mb-3 flex cursor-pointer items-start gap-2 text-[13px]">
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
                   className="mt-0.5 h-4 w-4 rounded border-border accent-[hsl(var(--primary))]" />
            <span>I checked these {ready.length} {noun}{ready.length === 1 ? '' : 's'}: {totalLabel} will leave the Airwallex wallet.</span>
          </label>
        )}
        <div className="flex items-center gap-3">
          {phase === 'done' || phase === 'check'
            ? <button onClick={onClose} disabled={phase === 'check'} className="text-[13px] text-muted-foreground hover:underline disabled:opacity-50">{phase === 'done' ? 'Close' : 'Cancel'}</button>
            : phase === 'confirm' && <button onClick={onClose} className="text-[13px] text-muted-foreground hover:underline">Cancel</button>}
          {phase === 'confirm' && (
            <Button className="ml-auto" onClick={payAll} disabled={!checked || ready.length === 0}>
              <Zap className="h-4 w-4" /> Pay {ready.length} via Airwallex{totalLabel ? ` · ${totalLabel}` : ''}
            </Button>
          )}
          {phase === 'pay' && <span className="ml-auto inline-flex items-center gap-2 text-[13px] text-muted-foreground"><PetanqueLoader size={18} /> paying…</span>}
          {phase === 'check' && <span className="ml-auto inline-flex items-center gap-2 text-[13px] text-muted-foreground"><PetanqueLoader size={18} /> checking…</span>}
          {phase === 'done' && <Badge variant={counts.failed ? 'warning' : 'success'} className="ml-auto">{counts.sent || 0} sent</Badge>}
        </div>
      </div>
    </Dialog>
  );
}
